import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, FileText, Clock, DollarSign, AlertCircle, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { feeLabel } from '../lib/visaFees';
import SuccessModal from '../components/SuccessModal';
import AppointmentModal from '../components/AppointmentModal';

const destinationImages = {
  schengen: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1200",
  canada: "https://images.unsplash.com/photo-1517935706615-2717063c2225?w=1200",
  usa: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=1200",
  dubai: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200",
  south_africa: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=1200",
  china: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=1200",
  india: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1200"
};

export const VisaDetailPage = () => {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVisaType, setSelectedVisaType] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    travel_date: '',
    visa_urgency: 'normal',
    additional_info: ''
  });

  useEffect(() => {
    fetchDestination();
  }, [destinationId]);

  const fetchDestination = async () => {
    try {
      const response = await api.get(`/visa-destinations/${destinationId}`);
      setDestination(response.data);
    } catch (error) {
      console.error('Error fetching destination:', error);
      toast.error('Destination non trouvée');
      navigate('/visa');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (visaType) => {
    // For Dubai Investor visa, open appointment modal (like other destinations)
    if (destinationId === 'dubai' && visaType.id === 'investor') {
      setSelectedVisaType(visaType);
      setShowAppointmentModal(true);
      return;
    }
    // For Dubai Visitor visa, redirect to multi-step workflow
    if (destinationId === 'dubai') {
      navigate(`/visa/dubai/apply?type=${encodeURIComponent(visaType.name)}&typeId=${visaType.id}`);
      return;
    }
    // For all other destinations, open appointment modal
    setSelectedVisaType(visaType);
    setShowAppointmentModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/visa-applications', {
        destination_id: destinationId,
        visa_type_id: selectedVisaType.id,
        applicant_name: formData.applicant_name,
        applicant_email: formData.applicant_email,
        applicant_phone: formData.applicant_phone,
        travel_date: formData.travel_date,
        visa_urgency: formData.visa_urgency,
        form_data: { additional_info: formData.additional_info }
      });
      setShowApplicationForm(false);
      setFormData({
        applicant_name: '', applicant_email: '', applicant_phone: '',
        travel_date: '', visa_urgency: 'normal', additional_info: ''
      });
      setShowSuccess(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  const renderFees = (fees) => {
    if (fees.contact_required) {
      return (
        <div className="flex items-center gap-2 text-amber-400">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">Nous contacter pour les tarifs</span>
        </div>
      );
    }
    return (
      <div className="divide-y divide-white/10">
        {Object.entries(fees).map(([key, value]) => (
          <div key={key} className="flex justify-between items-center py-2">
            <span className="text-[#94A3B8] text-xs">{feeLabel(key)}</span>
            <span className="font-semibold text-[#D4AF37] text-xs">{typeof value === 'number' ? `${value}$` : value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  if (!destination) return null;

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[22vh] min-h-[160px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${destinationImages[destinationId] || destinationImages.schengen})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-[#020617]/50" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full">
          <button
            onClick={() => navigate('/visa')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-3"
            data-testid="back-to-visa"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl md:text-5xl font-bold text-white mb-2">
              Visa <span className="text-[#D4AF37] italic">{destination.country}</span>
            </h1>
            <p className="text-sm md:text-base text-[#94A3B8] max-w-2xl">
              {destination.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Visa Types - Single Column */}
      <section className="py-6 md:py-10" data-testid="visa-types-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-base md:text-2xl font-bold text-white mb-4">
            Types de Visa <span className="text-[#D4AF37] italic">Disponibles</span>
          </h2>

          <div className="flex flex-col gap-6">
            {destination.visa_types?.map((visaType, index) => (
              <motion.div
                key={visaType.id}
                className="card-luxury overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`visa-type-${visaType.id}`}
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 px-4 py-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#D4AF37]" />
                    <h3 className="text-sm font-semibold text-white">{visaType.name}</h3>
                  </div>
                </div>

                <div className="p-4">
                  <p className="text-[#94A3B8] text-xs pb-3 border-b border-white/10">{visaType.description}</p>

                  {/* Fees */}
                  <div className="py-3 border-b border-white/10">
                    <h4 className="font-semibold text-white mb-2 flex items-center gap-2 text-xs">
                      <DollarSign className="w-3 h-3 text-[#D4AF37]" />
                      Tarifs
                    </h4>
                    {renderFees(visaType.fees)}
                  </div>

                  {/* Payment Terms - right after fees */}
                  {visaType.payment_terms && (
                    <div className="py-3 border-b border-white/10">
                      <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg p-3">
                        <h4 className="font-semibold text-[#D4AF37] mb-1 text-xs flex items-center gap-2">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Modalité de Paiement
                        </h4>
                        <p className="text-white text-xs">{visaType.payment_terms}</p>
                      </div>
                    </div>
                  )}

                  {/* Special Note - right after payment terms */}
                  {visaType.note && (
                    <div className="py-3 border-b border-white/10">
                      <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3">
                        <p className="text-sky-300 text-xs italic flex items-start gap-2">
                          <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          {visaType.note}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  {visaType.duration && (
                    <div className="py-3 border-b border-white/10">
                      <h4 className="font-semibold text-white mb-1 flex items-center gap-2 text-xs">
                        <Clock className="w-3 h-3 text-[#D4AF37]" />
                        Durée
                      </h4>
                      <p className="text-[#94A3B8] text-xs">{visaType.duration}</p>
                    </div>
                  )}

                  {/* Documents */}
                  {visaType.documents_required && visaType.documents_required.length > 0 && (
                    <div className="py-3 border-b border-white/10">
                      <h4 className="font-semibold text-white mb-2 text-xs">Documents Requis</h4>
                      <ul className="divide-y divide-white/5">
                        {visaType.documents_required.map((doc, i) => (
                          <li key={i} className="flex items-start gap-2 text-[#94A3B8] text-xs py-1.5">
                            <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                            <span>{doc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Conditions / Notes */}
                  {visaType.conditions && visaType.conditions.length > 0 && (
                    <div className="py-3 border-b border-white/10">
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                        <h4 className="font-semibold text-amber-400 mb-1 text-xs">
                          {visaType.conditions[0]?.toLowerCase().includes('note importante') ? 'Note importante' : 'Services Inclus'}
                        </h4>
                        <ul className="divide-y divide-amber-500/10">
                          {visaType.conditions.map((cond, i) => (
                            <li key={i} className="text-amber-300/80 text-xs py-1 flex items-start gap-2">
                              <Check className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                              <span>{cond.replace(/^Note importante\s*:\s*/i, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Destinations (for Residence Permanente) */}
                  {visaType.destinations && visaType.destinations.length > 0 && (
                    <div className="py-3 border-b border-white/10">
                      <h4 className="font-semibold text-white mb-2 text-xs">Destinations disponibles</h4>
                      <div className="flex flex-wrap gap-2">
                        {visaType.destinations.map((dest, i) => (
                          <span key={i} className="px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded text-[#D4AF37] text-xs">
                            {dest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-3">
                    <button
                      className="w-full btn-gold py-2.5 text-xs flex items-center justify-center gap-2"
                      onClick={() => handleApply(visaType)}
                      data-testid={`apply-visa-${visaType.id}`}
                    >
                      {destinationId === 'dubai' && visaType.id !== 'investor' ? (
                        <>Démarrer la Procédure</>
                      ) : (
                        <>
                          <Calendar className="w-3.5 h-3.5" />
                          Prendre Rendez-vous
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Dialog */}
      <Dialog open={showApplicationForm} onOpenChange={setShowApplicationForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-sm">Demande de {selectedVisaType?.name}</DialogTitle>
            <DialogDescription className="text-[#94A3B8] text-xs">
              Destination: {destination.country}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="visa-application-form">
            <div>
              <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
              <Input
                value={formData.applicant_name}
                onChange={(e) => setFormData({...formData, applicant_name: e.target.value})}
                required placeholder="Votre nom complet"
                className="bg-white/5 border-white/10 text-white text-xs h-9"
                data-testid="input-name"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Email *</Label>
              <Input
                type="email"
                value={formData.applicant_email}
                onChange={(e) => setFormData({...formData, applicant_email: e.target.value})}
                required placeholder="votre@email.com"
                className="bg-white/5 border-white/10 text-white text-xs h-9"
                data-testid="input-email"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
              <Input
                value={formData.applicant_phone}
                onChange={(e) => setFormData({...formData, applicant_phone: e.target.value})}
                required placeholder="+243..."
                className="bg-white/5 border-white/10 text-white text-xs h-9"
                data-testid="input-phone"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Date de Voyage Prévue</Label>
              <Input
                type="date"
                value={formData.travel_date}
                onChange={(e) => setFormData({...formData, travel_date: e.target.value})}
                className="bg-white/5 border-white/10 text-white text-xs h-9"
                data-testid="input-date"
              />
            </div>

            {selectedVisaType?.fees.express && (
              <div>
                <Label className="text-[#94A3B8] text-xs">Urgence</Label>
                <Select
                  value={formData.visa_urgency}
                  onValueChange={(value) => setFormData({...formData, visa_urgency: value})}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="select-urgency">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="normal" className="text-xs">Normal ({selectedVisaType.fees.normal}$)</SelectItem>
                    <SelectItem value="express" className="text-xs">Express ({selectedVisaType.fees.express}$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[#94A3B8] text-xs">Informations Supplémentaires</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                placeholder="Toute information utile pour votre demande..."
                rows={2}
                className="bg-white/5 border-white/10 text-white text-xs resize-none"
                data-testid="input-info"
              />
            </div>

            <div className="flex gap-3 pt-3">
              <button
                type="button"
                className="flex-1 btn-glass py-2.5 text-white text-xs"
                onClick={() => setShowApplicationForm(false)}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 btn-gold py-2.5 text-xs"
                disabled={submitting}
                data-testid="submit-application"
              >
                {submitting ? 'Envoi...' : 'Soumettre la Demande'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Appointment Modal for non-Dubai destinations */}
      <AppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        destination={destination?.country || ''}
        visaType={selectedVisaType?.name || ''}
      />

      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)}
        title="Demande de visa envoyée !"
        message="Votre demande de visa a été transmise avec succès. Notre équipe traitera votre dossier et vous contactera rapidement."
      />
    </div>
  );
};

export default VisaDetailPage;
